import { translate } from '@/utilities';
import { format, isThisYear, fromUnixTime, getDay, getUnixTime, isBefore, addDays } from 'date-fns';

// TODO translate short and long time signatures
const msPerSecond = 1000;
const msPerMinute = 60 * msPerSecond;
const msPerHour = msPerMinute * 60;
const msPerDay = msPerHour * 24;
const msPerMonth = msPerDay * 30;
const msPerYear = msPerDay * 365;

const periods = [
  {
    value: msPerYear,
    labelPlural: 'years',
    labelSingular: 'a year',
    shortFormat: 'Y',
  },
  {
    value: msPerMonth,
    labelPlural: 'months',
    labelSingular: 'a month',
    shortFormat: 'M',
  },
  {
    value: msPerDay,
    labelPlural: 'days',
    labelSingular: 'a day',
    shortFormat: 'd',
  },
  {
    value: msPerHour,
    labelPlural: 'hours',
    labelSingular: 'an hour',
    shortFormat: 'h',
  },
  {
    value: msPerMinute,
    labelPlural: 'minutes',
    labelSingular: 'a minute',
    shortFormat: 'm',
  },
  {
    value: msPerSecond,
    labelPlural: 'seconds',
    labelSingular: 'a second',
    shortFormat: 's',
  },
];

const monthsLabels = translate('monthsAbbreviation')?.split(',');
// As line above is not reactive and can return undefind if utility is imported before translation was loaded,
// this method is added to ensure that monthsLabels retires to get the value from translation.
const generateMonthsLabels = () => {
  return monthsLabels || translate('monthsAbbreviation')?.split(',') || [];
};

const generateLabel = (value: number, periodIndex: number, shortFormat: boolean): string => {
  const period = periods[periodIndex];

  if (shortFormat) {
    return `${value}${period.shortFormat}`;
  }
  return value <= 1 ? period.labelSingular : `${value} ${period.labelPlural}`;
};

const getTimeDifferenceRecursion = (
  elapsed: number,
  shortFormat: boolean,
  periodIndex = 0,
  valuesArray = [] as any
): any => {
  if (elapsed <= msPerMinute) {
    valuesArray.push({
      label: translate('nowActivity'),
    });
    return valuesArray;
  }

  if (elapsed >= periods[periodIndex].value) {
    const value = Math.floor(elapsed / periods[periodIndex].value);
    valuesArray.push({
      label: generateLabel(value, periodIndex, shortFormat),
    });
    const rest = elapsed % periods[periodIndex].value;
    const newPeriodIndex = periodIndex + 1;
    return getTimeDifferenceRecursion(rest, shortFormat, newPeriodIndex, valuesArray);
  }
  return getTimeDifferenceRecursion(elapsed, shortFormat, periodIndex + 1, valuesArray);
};

const timeDifference = (time: number, depth = 2, dueDate = false, shortFormat = false) => {
  const current = Date.now();

  let elapsed = 0;
  if (dueDate) {
    elapsed = time - current;
  } else {
    elapsed = current - time;
  }

  const array = getTimeDifferenceRecursion(elapsed, shortFormat, 0, []);
  let finalString = '';
  const loopLimit = array.length < depth ? array.length : depth;
  for (let i = 0; i < loopLimit; i += 1) {
    let separator = ', ';
    if (i === loopLimit - 1 && i !== 0) {
      separator = ' and ';
    }
    finalString += i === 0 ? `${array[i].label}` : `${separator} ${array[i].label}`;
  }

  if (dueDate) {
    return `in ${finalString}`;
  } else {
    return shortFormat || finalString === translate('nowActivity')
      ? `${finalString}`
      : `${finalString} ago`;
  }
};

//16 Mar
const convertTimestampToDate = (time: number) => {
  if (!isValidTimestamp(time)) return '';
  const date = fromUnixTime(time);
  return `${format(date, 'd')} ${generateMonthsLabels()[date.getMonth()]}`;
};

// Mon 16 Mar
const convertTimestampToDateSecondFormatWithDay = (time: number) => {
  if (!isValidTimestamp(time)) return '';
  const days = translate('daysAbbreviationLong')?.split(',');
  const dayIndex = getDay(fromUnixTime(time)) - 1;
  return `${days.at(dayIndex)} ${convertTimestampToDate(time)}`;
};

//28 Jun, 11:36 (current year) / 28 Jun, 2022, 11:36 (not current year)
const convertTimestampToDDMlabelTime = (time: number) => {
  if (!isValidTimestamp(time)) return '';
  const date = fromUnixTime(time);
  const dateFormat = `d MMM, ${isThisYear(date) ? '' : 'yyyy, '}HH:mm`;
  return format(date, dateFormat);
};

// 02:21
const convertToHoursMinutes = (time: number) => {
  if (!isValidTimestamp(time)) return '';
  const date = fromUnixTime(time);
  return format(date, 'HH:mm');
};

// 16 March, 2021
const timestampToFullDate = (time: number) => {
  if (!isValidTimestamp(time)) return '';
  const date = fromUnixTime(time);
  const months = translate('months')?.split(',');
  const month = months[date.getMonth()];
  return `${format(date, 'dd')} ${month}, ${format(date, 'yyyy')}`;
};

// 16 Mar, 2024
const timestampToFullDateShort = (time: number) => {
  if (!isValidTimestamp(time)) return '';
  const date = fromUnixTime(time);
  return `${format(date, 'dd')} ${generateMonthsLabels()[date.getMonth()]}, ${format(date, 'yyyy')}`;
};

// 06/05/24
const convertDateToDDMMYY = (time: number) => {
  if (!isValidTimestamp(time)) return '';
  const date = fromUnixTime(time);
  return format(date, 'dd/MM/yy');
};

// 06/05
const convertDateToDDMM = (time: number) => {
  if (!isValidTimestamp(time)) return '';
  const date = fromUnixTime(time);
  return format(date, 'dd/MM');
};

const isValidTimestamp = (time: number) => {
  return !isNaN(time) && time > 0;
};

const getCurrentDateInSeconds = () => {
  return getUnixTime(new Date());
};

const isDateWithinNDays = (date: number, days: number) => {
  if (!isValidTimestamp(date)) return false;
  return isBefore(fromUnixTime(date), addDays(new Date(), days));
};

export {
  timeDifference,
  convertTimestampToDate,
  convertTimestampToDateSecondFormatWithDay,
  timestampToFullDate,
  timestampToFullDateShort,
  convertTimestampToDDMlabelTime,
  convertToHoursMinutes,
  convertDateToDDMM,
  convertDateToDDMMYY,
  getCurrentDateInSeconds,
  isDateWithinNDays,
};
